import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import callRemote from '../../../utils/remote';
import { isValidEmail } from '../../../utils/validators';
import {
  ChangeEmailFormContainer,
  ChangeEmailFormTitle,
  EmailInput,
  EmailInputLabel,
  SubmitButton,
  CancelButton,
  ButtonsContainer,
  FormError,
} from './styles';
import texts from './texts.json';

const ChangeEmailForm = ({
  sessionId,
  currentEmail,
  onCancel,
  onSuccess,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Check if form is valid to enable submit button
  const isFormValid = Boolean(
    newEmail.trim()
    && isValidEmail(newEmail)
    && newEmail.toLowerCase().trim() !== (currentEmail || '').toLowerCase().trim()
    && newEmail.trim() === confirmEmail.trim(),
  );

  const validateEmails = useCallback(() => {
    if (!newEmail.trim()) {
      setError(texts.emailRequired);
      return false;
    }

    if (!isValidEmail(newEmail)) {
      setError(texts.invalidEmailFormat);
      return false;
    }

    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setError(texts.sameEmailError);
      return false;
    }

    if (newEmail !== confirmEmail) {
      setError(texts.emailsDoNotMatch);
      return false;
    }

    return true;
  }, [newEmail, confirmEmail, currentEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateEmails()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await callRemote(
        '',
        'userProfiles-changeOnboardingEmail',
        {
          checkoutSessionOnboardingId: sessionId,
          currentEmail: currentEmail.trim(),
          newEmail: newEmail.toLowerCase().trim(),
        },
      );

      await response.json();

      if (!response.ok) {
        // Handle specific error: email already in use (409 Conflict)
        if (response.status === 409) {
          setError(texts.emailAlreadyInUse);
        } else {
          setError(texts.changeEmailError);
        }
        return;
      }

      onSuccess(newEmail.toLowerCase().trim());
    } catch (err) {
      setError(texts.changeEmailError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ChangeEmailFormContainer as="form" onSubmit={handleSubmit}>
      <ChangeEmailFormTitle>{texts.changeEmailTitle}</ChangeEmailFormTitle>

      <EmailInputLabel htmlFor="newEmail">{texts.newEmailLabel}</EmailInputLabel>
      <EmailInput
        id="newEmail"
        type="email"
        placeholder={texts.newEmailPlaceholder}
        value={newEmail}
        onChange={(e) => {
          setNewEmail(e.target.value);
          setError('');
        }}
        disabled={isSubmitting}
      />

      <EmailInputLabel htmlFor="confirmEmail">{texts.confirmEmailLabel}</EmailInputLabel>
      <EmailInput
        id="confirmEmail"
        type="email"
        placeholder={texts.confirmEmailPlaceholder}
        value={confirmEmail}
        onChange={(e) => {
          setConfirmEmail(e.target.value);
          setError('');
        }}
        disabled={isSubmitting}
      />

      {!isFormValid && confirmEmail && <FormError>{texts.emailsDoNotMatch}</FormError>}

      {error && <FormError>{error}</FormError>}

      <ButtonsContainer>
        <CancelButton type="button" onClick={onCancel} disabled={isSubmitting}>
          {texts.cancel}
        </CancelButton>
        <SubmitButton type="submit" disabled={isSubmitting || !isFormValid}>
          {isSubmitting ? texts.submitting : texts.confirmChange}
        </SubmitButton>
      </ButtonsContainer>
    </ChangeEmailFormContainer>
  );
};

ChangeEmailForm.propTypes = {
  sessionId: PropTypes.string.isRequired,
  currentEmail: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default ChangeEmailForm;
